import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { cPath } from "../constants";
import { Container } from "../components/layout/Container";
import useSEO from "../hooks/useSEO";
import seoConfig from "../config/seoConfig";
import { generateArticleSchema, generateBreadcrumbSchema } from "../utils/schemaGenerators";
import { getBlogPostBySlug } from "../services/blogServices";
import BlogArticleBody from "../components/blog/BlogArticleBody";

const Hero = styled.section`
  background: hsl(${({ theme }) => theme.colors.navDark});
  padding: 2rem 0;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
`;

const H1 = styled.h1`
  margin: 0 0 0.5rem;
  font-size: clamp(1.75rem, 1.2rem + 2vw, 2.75rem);
  font-weight: 800;
  line-height: 1.2;
`;

const Body = styled(Container)`
  padding: 2rem 1rem 3rem;
  max-width: ${({ theme }) => theme.layout.proseMax};
`;

const ExcerptLead = styled.p`
  margin: 0 0 1.25rem;
  font-size: 1.0625rem;
  line-height: 1.65;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const Back = styled(Link)`
  display: inline-block;
  margin-bottom: 1.5rem;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.primary});
`;

const LoadNote = styled.p`
  text-align: center;
  padding: 2rem 1rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    setPost(null);
    (async () => {
      try {
        const row = await getBlogPostBySlug(slug || "");
        if (cancel) return;
        if (!row) setNotFound(true);
        else setPost(row);
      } catch {
        if (!cancel) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  const seo = useSEO({
    title: post?.title,
    description: post?.excerpt,
    image: post?.image,
    titleTemplate: "blog",
    schema: post
      ? [
          generateArticleSchema({
            title: post.title,
            excerpt: post.excerpt,
            featuredImage: post.image,
            createdAt: post.publishedAt || post.createdAt,
            updatedAt: post.updatedAt,
          }),
          generateBreadcrumbSchema([
            { name: "Home", url: seoConfig.siteUrl },
            { name: "Blog", url: `${seoConfig.siteUrl}/blog` },
            {
              name: post.title,
              url: `${seoConfig.siteUrl}/blog/${post.slug || slug}`,
            },
          ]),
        ].filter(Boolean)
      : undefined,
    noindex: !loading && !post,
  });

  if (loading) {
    return (
      <>
        {seo}
        <LoadNote>Loading article…</LoadNote>
      </>
    );
  }

  if (notFound || !post) {
    return (
      <>
        {seo}
        <Body>
          <h1>Article not found</h1>
          <Link to={cPath("blog")}>← Back to Saving Tips</Link>
        </Body>
      </>
    );
  }

  return (
    <>
      {seo}
      <Hero>
        <Container>
          <Back to={cPath("blog")}>← All articles</Back>
          <H1>{post.title}</H1>
          <p style={{ margin: 0, opacity: 0.7, fontSize: "0.875rem" }}>
            {post.date} · {post.readMins} min read
          </p>
        </Container>
      </Hero>
      <Body>
        {post.excerpt ? <ExcerptLead>{post.excerpt}</ExcerptLead> : null}
        <BlogArticleBody content={post.content} />
      </Body>
    </>
  );
}
